import { FC, useMemo } from 'react';

import { DownloadIcon, XIcon } from '@heroicons/react/outline';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';

import { formatDate } from '../../utils/delivery.utils';
import {
  computeRebateTotals,
  RebateRowCalc,
  settlementLineToRebateRow,
  sortRebateRowsByDeliveredAt,
} from '../../utils/rebate-table.utils';
import { exportRebateSettlementDetailExcel } from '../../utils/rebateSettlementExcel';
import { statusColor, statusLabel } from '../../utils/rebate-settlement.utils';
import { RebateSettlementTable } from './RebateSettlementTable';

// ─────────────────────────────────────────────
// RebateHistoryDetailModal
//
// 정산 완료 내역의 한 달(정산 문서) 상세를 모달로 보여준다.
// 정산 예정 테이블과 동일한 RebateSettlementTable 을 그대로 사용해
// 상품/구매자/배송일/고인명/지도사/금액까지 모두 노출한다.
// ─────────────────────────────────────────────

/** `RebateHistoryDetailModal` 컴포넌트의 props */
export interface RebateHistoryDetailModalProps {
  /** 상세를 볼 정산 문서 */
  settlement: GiftRebateSettlement;
  /** 닫기 콜백 */
  onClose: () => void;
}

export const RebateHistoryDetailModal: FC<RebateHistoryDetailModalProps> = ({
  settlement,
  onClose,
}) => {
  const rows = useMemo<RebateRowCalc[]>(
    () =>
      sortRebateRowsByDeliveredAt(settlement.lines.map(settlementLineToRebateRow)),
    [settlement],
  );
  const totals = useMemo(() => computeRebateTotals(rows), [rows]);

  // 정산일(완료) / 정산 예정일(미지급) — executedAt 유무로 구분
  const hasExecuted = !!settlement.executedAt;
  const payoutDate = hasExecuted
    ? new Date(settlement.executedAt as Date)
    : // 예정 지급일: month+1 (12월이면 다음해 1월) 15일
      new Date(
        settlement.month === 12 ? settlement.year + 1 : settlement.year,
        settlement.month === 12 ? 0 : settlement.month,
        15,
      );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative my-4 flex max-h-[92vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
              {settlement.year}년 {settlement.month}월 정산 상세
              <span
                className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusColor(settlement.status)}`}
              >
                {statusLabel(settlement.status)} · {formatDate(payoutDate)}
              </span>
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              정산에 포함된 배송 내역입니다.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportRebateSettlementDetailExcel(settlement)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <DownloadIcon className="h-4 w-4" />
              엑셀 다운로드
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="닫기"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 본문 (요약 카드 + 테이블) */}
        <div className="overflow-auto px-5 py-4">
          {/* 요약 — 총 수익금을 히어로로, 판매가/원가/수수료는 보조(수식)로 표기 */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-gray-50 px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <span>
                총 판매가{' '}
                <span className="font-semibold text-gray-700">
                  {totals.totalPriceRetail.toLocaleString()}
                </span>
              </span>
              <span className="text-gray-300">−</span>
              <span>
                총 상품원가{' '}
                <span className="font-semibold text-gray-700">
                  {totals.totalPriceWhole.toLocaleString()}
                </span>
              </span>
              <span className="text-gray-300">−</span>
              <span>
                총 수수료{' '}
                <span className="font-semibold text-gray-700">
                  {totals.totalFee.toLocaleString()}
                </span>
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-gray-500">총 수익금</span>
              <span className="text-3xl font-bold text-rose-700">
                {totals.totalAmount.toLocaleString()}
              </span>
              <span className="text-xs text-gray-400">원 (VAT 포함)</span>
            </div>
          </div>

          <RebateSettlementTable
            rows={rows}
            totals={totals}
            emptyMessage="정산 내역이 없습니다."
          />
        </div>
      </div>
    </div>
  );
};
